import { AssistantService } from '@/api/services/assistant.service';
import type { IAssistantConfigResponse } from '@/interfaces/assistant.interface';
import type { IGhHostView, IGhMenuItem } from '../gpthelp-host-contract';
import { ACTIVITY_VIEW_LIMIT, activityView } from '../cards/activity-cards';
import { adminCardFormat } from '../cards/admin-card-format';
import { howItWorksView } from '../cards/how-it-works-view';
import { buildGpthelpToolGate, supportUrlOf } from '../tools/tool-gate';
import { ensureRestaurantScopeOrThrow } from '../tools/tool-scope';
import type {
  ICartadigitalAdminHostToolDeps,
  IGpthelpToolGate,
} from '../tools/tool-types';
import { activityContextFor } from './assistant-activity';
import { supportIconOf } from './assistant-support';

/** What the menu actions need from the admin. */
export interface IAssistantMenuHost {
  deps: ICartadigitalAdminHostToolDeps;
  /** Opens a link outside the admin (the in-app browser in the APK). */
  openExternal(url: string): Promise<void>;
}

/** «Cambios del asistente» is for the members who can change the menu, writes on or off. */
function canSeeActivity(gate: IGpthelpToolGate): boolean {
  return (
    gate.hasRestaurantScope && (gate.canManageCatalog || gate.canDisableCatalog)
  );
}

/**
 * «Cambios del asistente» (independent of writes, so undo stays reachable),
 * «Hablar con una persona» when there is a support link, «Cómo funciona».
 * None outside a store (the bubble on the logged-out pages, the store list
 * and `/me/*`): every item needs one, so the bubble shows no ⋯ there.
 */
export function assistantMenuItems(
  gate: IGpthelpToolGate,
  config: IAssistantConfigResponse | null,
  t: (key: string) => string
): IGhMenuItem[] {
  if (!gate.hasRestaurantScope) return [];
  const items: IGhMenuItem[] = [];
  if (canSeeActivity(gate))
    items.push({
      id: 'activity',
      label: t('assistant.menu.activity'),
      icon: 'history',
    });
  const supportUrl = supportUrlOf(config);
  if (supportUrl)
    items.push({
      id: 'human',
      label: t('assistant.menu.human'),
      icon: supportIconOf(supportUrl),
    });
  items.push({
    id: 'howItWorks',
    label: t('assistant.menu.howItWorks'),
    icon: 'info',
  });
  return items;
}

/**
 * Runs a ⋯ item: the activity view, the support link (no view) or «Cómo
 * funciona». Unknown or no longer allowed items throw (the widget says it
 * could not complete).
 */
export async function runAssistantMenuAction(
  id: string,
  config: IAssistantConfigResponse | null,
  host: IAssistantMenuHost
): Promise<{ view?: IGhHostView } | void> {
  const scope = ensureRestaurantScopeOrThrow(host.deps);
  const fmt = adminCardFormat(host.deps, scope.timeZone);

  if (id === 'activity') {
    if (!canSeeActivity(buildGpthelpToolGate(host.deps)))
      throw new Error('This member cannot see the assistant changes.');
    const response = await AssistantService.listActions(scope.restaurantId, {
      limit: ACTIVITY_VIEW_LIMIT,
    });
    return {
      view: activityView(response, activityContextFor(host.deps, scope), fmt),
    };
  }
  if (id === 'human') {
    const url = supportUrlOf(config);
    if (!url) throw new Error('This store has no support link.');
    await host.openExternal(url);
    return;
  }
  if (id === 'howItWorks') return { view: howItWorksView(fmt) };
  throw new Error(`Unknown assistant menu item: ${id}`);
}
